import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { checkSession } from "../api/subscription";
import { isGuestUser } from "../utils/storage";
import Footer from "@/components/Footer";
import "../styles/globle.css";

interface CheckSessionResponse {
  payment_status: string;
  customer_email?: string;
  is_existing_user?: boolean;
  is_upgrade?: boolean;
}

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [isGuest, setIsGuest] = useState(false);
  const [isUpgrade, setIsUpgrade] = useState(false);

  useEffect(() => {
    const guestStatus = isGuestUser();
    setIsGuest(guestStatus);

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");

    if (!sessionId) {
      setStatus("error");
      return;
    }

    checkSession(sessionId)
      .then((data: CheckSessionResponse) => {
        if (
          data.is_upgrade ||
          data.payment_status === "paid" ||
          data.payment_status === "no_payment_required"
        ) {
          setStatus("success");
          setIsUpgrade(data.is_upgrade || false);

          // Only auto-redirect when this is a new purchase, same logic as before
          if (!data.is_upgrade) {
            setTimeout(() => {
              navigate("/verify-email");
            }, 2000);
          }
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, [navigate]);

  return (
    <div className="login-container">
      <div className="flex items-center justify-center w-full px-4 pt-12 pb-24 md:pt-16 md:pb-32">
        <Card className="w-full max-w-md p-8 rounded-2xl shadow-sm">
          {/* HEADER — aligned with Login card */}
          <CardHeader className="text-center space-y-1">
            {status === "loading" && (
              <>
                <CardTitle className="text-2xl font-bold">
                  Verifying your payment
                </CardTitle>
                <CardDescription className="text-base text-gray-600">
                  Verifying payment...
                </CardDescription>
              </>
            )}

            {status === "success" && (
              <>
                <div className="text-6xl mb-2">
                  {isUpgrade ? "🚀" : "🎉"}
                </div>
                <CardTitle className="text-2xl font-bold">
                  {isUpgrade ? "Upgrade successful" : "Payment successful"}
                </CardTitle>
                <CardDescription className="text-base text-gray-600">
                  {isUpgrade
                    ? "Your plan has been upgraded successfully."
                    : isGuest
                    ? "Setting up your premium account..."
                    : "Finalizing your account setup..."}
                </CardDescription>
              </>
            )}

            {status === "error" && (
              <>
                <div className="text-6xl mb-2">📧</div>
                <CardTitle className="text-2xl font-bold">
                  Payment complete
                </CardTitle>
                <CardDescription className="text-base text-gray-600">
                  Your payment went through, but we couldn’t verify your account
                  automatically. Please check your email to finish setting up
                  your password.
                </CardDescription>
              </>
            )}
          </CardHeader>

          {/* CONTENT — only needed for the CTA button when upgrading */}
          {status === "success" && isUpgrade && (
            <CardContent className="pt-6">
              <button
                onClick={() => navigate("/dashboard")}
                className="
                  w-full h-11
                  text-base font-medium rounded-lg
                  bg-[#005FCC] hover:bg-[#0049A8]
                  text-white
                  transition-colors
                "
              >
                Go to dashboard
              </button>
            </CardContent>
          )}
        </Card>
      </div>

      <div className="footer-container-login">
        <Footer />
      </div>
    </div>
  );
};

export default PaymentSuccess;
